import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldAlert, MapPin, KeyRound, Building2 } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { api, setToken } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { AUTH } from '@/constants/testIds';
import { R } from '@/lib/routes';
import { landingFor } from '@/constants/modules';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [phase, setPhase] = useState('processing'); // processing | 2fa | geo | error
  const [challengeId, setChallengeId] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [otp, setOtp] = useState('');
  const [msg, setMsg] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [coords, setCoords] = useState(null);
  const [emailSent, setEmailSent] = useState(true);
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  const getCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ location_denied: true });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve({ location_denied: true }),
        { timeout: 10000, enableHighAccuracy: false }
      );
    });

  const runCallback = async (sid, loc) => {
    try {
      const { data } = await api.post('/auth/google/callback', { session_id: sid, ...loc });
      if (data.requires_2fa) {
        setChallengeId(data.challenge_id);
        setRecipients(data.otp_recipients || []);
        setEmailSent(data.email_sent !== false);
        setCooldown(30);
        setPhase('2fa');
      } else {
        setToken(data.token);
        setUser(data.user);
        const dest = landingFor(data.user);
        window.history.replaceState(null, '', dest);
        navigate(dest, { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = detail?.code;
      if (code === 'geo_denied') {
        setMsg(detail.message);
        setPhase('geo');
      } else {
        setMsg(detail?.message || detail || 'Sign-in failed. Please try again.');
        setPhase('error');
      }
    }
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace('#', '')).get('session_id');
    if (!sid) {
      navigate(R.login, { replace: true });
      return;
    }
    setSessionId(sid);
    (async () => {
      const loc = await getCoords();
      setCoords(loc);
      await runCallback(sid, loc);
    })();
    // eslint-disable-next-line
  }, []);

  const verify = async () => {
    if (otp.length !== 6) return;
    setVerifying(true);
    try {
      const { data } = await api.post('/auth/2fa/verify', { challenge_id: challengeId, otp });
      setToken(data.token);
      setUser(data.user);
      const dest = landingFor(data.user);
      window.history.replaceState(null, '', dest);
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Invalid code');
      setOtp('');
    } finally {
      setVerifying(false);
    }
  };

  const retryGeo = async () => {
    setPhase('processing');
    const loc = await getCoords();
    setCoords(loc);
    await runCallback(sessionId, loc);
  };

  // Countdown for the resend cooldown.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    if (cooldown > 0 || resending || !challengeId) return;
    setResending(true);
    try {
      const { data } = await api.post('/auth/2fa/resend', { challenge_id: challengeId });
      if (data.otp_recipients) setRecipients(data.otp_recipients);
      setEmailSent(data.email_sent !== false);
      setOtp('');
      setCooldown(data.cooldown || 30);
      if (data.email_sent === false) toast.error('Could not send the email. Please try again shortly.');
      else toast.success('A new verification code has been sent.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'cooldown' && detail?.retry_after) setCooldown(detail.retry_after);
      toast.error(detail?.message || 'Could not resend the code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="kesar-bg flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-[0_20px_70px_rgba(0,0,0,0.08)] sm:p-10">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#245329] text-white">
            <Building2 className="h-6 w-6" strokeWidth={2.2} />
          </div>
          <span className="font-head text-xl font-extrabold text-stone-900">KesarOne</span>
        </div>

        {phase === 'processing' && (
          <div className="py-10 text-center">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#245329]" />
            <p className="mt-4 text-sm font-semibold text-stone-600">Verifying identity & location…</p>
          </div>
        )}

        {phase === '2fa' && (
          <div>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E6F4EA] text-[#245329]">
              <KeyRound className="h-6 w-6" />
            </div>
            <h1 className="font-head text-2xl font-extrabold tracking-tight text-stone-900">Two-factor verification</h1>
            <p className="mt-2 text-sm font-medium text-stone-500">
              A 6-digit code was sent to security {recipients.length ? `(${recipients.join(', ')})` : 'email'}. Enter it below to continue.
            </p>
            {!emailSent && (
              <div data-testid="auth-otp-email-warning" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
                We couldn't confirm the email was sent. Use “Resend code” below to try again.
              </div>
            )}
            <div className="mt-6 flex justify-center" data-testid={AUTH.otpInput}>
              <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <button
              data-testid={AUTH.otpVerifyBtn}
              onClick={verify}
              disabled={otp.length !== 6 || verifying}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#245329] px-6 py-3 text-sm font-bold text-white shadow-[0_4px_12px_rgba(36,83,41,0.22)] transition hover:bg-[#1c4220] active:scale-[0.99] disabled:opacity-50"
            >
              {verifying && <Loader2 className="h-4 w-4 animate-spin" />} Verify & continue
            </button>
            <div className="mt-4 flex items-center justify-center gap-1 text-sm">
              <span className="font-medium text-stone-400">Didn't receive it?</span>
              <button
                data-testid={AUTH.otpResendBtn}
                onClick={resend}
                disabled={cooldown > 0 || resending}
                className="font-bold text-[#245329] transition hover:text-[#1c4220] disabled:cursor-not-allowed disabled:text-stone-300"
              >
                {resending ? 'Sending…' : cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          </div>
        )}

        {phase === 'geo' && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
              <MapPin className="h-7 w-7" />
            </div>
            <h1 className="font-head text-xl font-extrabold text-stone-900">Access Restricted</h1>
            <p className="mt-2 text-sm font-medium text-stone-500">{msg}</p>
            <button
              data-testid={AUTH.geoRetryBtn}
              onClick={retryGeo}
              className="mt-6 w-full rounded-xl bg-stone-900 px-6 py-3 text-sm font-bold text-white transition hover:bg-stone-800 active:scale-[0.99]"
            >
              Retry location check
            </button>
            <button onClick={() => navigate(R.login)} className="mt-3 text-sm font-semibold text-stone-500 hover:text-stone-800">
              Back to sign in
            </button>
          </div>
        )}

        {phase === 'error' && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
              <ShieldAlert className="h-7 w-7" />
            </div>
            <h1 className="font-head text-xl font-extrabold text-stone-900">Sign-in blocked</h1>
            <p className="mt-2 text-sm font-medium text-stone-500">{msg}</p>
            <button onClick={() => navigate(R.login)} className="mt-6 w-full rounded-xl bg-stone-900 px-6 py-3 text-sm font-bold text-white transition hover:bg-stone-800">
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
